import { Capacitor } from "@capacitor/core";
import { SocialLogin } from "@capgo/capacitor-social-login";
import { Box, type SxProps, type Theme } from "@mui/material";
import { useGoogleLogin } from "@react-oauth/google";
import { type UseMutationResult, useMutation } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { type FC, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthProvider";
import { useAnalytics } from "@/contexts/analytics/hooks";
import { useSnackbar } from "@/contexts/SnackbarContext";
import { AUTH_EVENT, AUTH_METHOD, authEventProps } from "@/pages/signin/authAnalytics";
import { backendService } from "@/services/backend.service";
import { GoogleIcon } from "../icons/GoogleIcon";
import { LoginButton } from "./common";

function useGoogleSSO(): UseMutationResult<void, Error, { code: string }, unknown> {
	const { t } = useTranslation();
	const { showSnackbar } = useSnackbar();
	const { saveTokenData } = useAuth();
	const { track, trackError } = useAnalytics();

	return useMutation({
		mutationKey: ["useGoogleSSO"],
		mutationFn: async ({ code }) => {
			try {
				track("login", { type: "SSO", provider: "google", status: "start" });

				const response = await backendService.googleSSO(code);
				if (response.status >= 400) {
					throw Error(response.statusText);
				}

				track("login", {
					type: "SSO",
					provider: "google",
					status: "validation",
				});

				const loginResult = await saveTokenData(response.data);
				if (!loginResult.isSuccessful) {
					throw Error("Failed to login");
				}

				track("login", { type: "SSO", provider: "google", status: "success" });
				track(AUTH_EVENT.LOGIN_SUCCESS, authEventProps(AUTH_METHOD.GOOGLE, true));
			} catch (e) {
				trackError("login", {
					type: "SSO",
					provider: "google",
					error: (e as Error).message,
				});
				trackError(AUTH_EVENT.LOGIN_FAILED, { ...authEventProps(AUTH_METHOD.GOOGLE, false), reason: (e as Error).message });

				if (e instanceof AxiosError && e.response?.data?.detail) {
					const { responseURL } = e.request || {};

					/** HACK: since `axiosWrapper` also calls `/refresh` on its own then we need to filter messages not related to the login */
					if (typeof responseURL === "string" && !responseURL.endsWith("/token/sso/google")) {
						showSnackbar(t("error.user.nonexistent.or.inactive"), "error");
					} else {
						const [key, ...params] = e.response.data.detail.split("|");
						const message = params.length > 0 ? t(key, { status: t(`status.${params[0].toLowerCase()}`) }) : t(e.response.data.detail);
						showSnackbar(message, "error");
					}
				} else {
					showSnackbar(e instanceof Error ? e.message : t("error.user.nonexistent.or.inactive"), "error");
				}
			}
		},
	});
}

export const GoogleOAuthLoginButton: FC<{ sx?: SxProps<Theme> }> = ({ sx }) => {
	const { t } = useTranslation();
	const { mutateAsync: signIn, isPending } = useGoogleSSO();
	const { showSnackbar } = useSnackbar();
	const { track, trackError } = useAnalytics();
	const isInitialized = useRef(false);
	const navigate = useNavigate();

	// Initialize Social Login once on component mount (only for native platforms)
	useEffect(() => {
		if (Capacitor.isNativePlatform() && !isInitialized.current) {
			SocialLogin.initialize({
				google: {
					webClientId: import.meta.env.VITE_GOOGLE_CLIENT_ID,
					iOSClientId: import.meta.env.VITE_GOOGLE_IOS_CLIENT_ID,
					iOSServerClientId: import.meta.env.VITE_GOOGLE_CLIENT_ID,
					mode: "offline",
				},
			})
				.then(() => {
					isInitialized.current = true;
				})
				.catch((error) => {
					console.error("Failed to initialize Social Login:", error);
				});
		}
	}, []);

	// Always call hook (it will only work on web, but we need to call it unconditionally)
	const googleOAuthLogin = useGoogleLogin({
		flow: "auth-code",
		onSuccess: async (response) => {
			try {
				await signIn(response);
				navigate("/");
			} catch {
				// Error already handled by useGoogleSSO's onError/catch
			}
		},
		onError: (errorType) => {
			console.error({ errorType });

			trackError("login", {
				type: "SSO",
				provider: "google",
				...errorType,
				action: "google oauth login",
			});
			showSnackbar(t("error.sso.login.failed"), "error");
		},
	});

	const handleGoogleSignIn = async (): Promise<void> => {
		track("login", {
			type: "SSO",
			provider: "google",
			action: "google oauth login",
		});

		if (Capacitor.isNativePlatform()) {
			try {
				// Sign in (initialization already happened in useEffect)
				const result = await SocialLogin.login({
					provider: "google",
					options: {
						scopes: ["profile", "email"],
					},
				});

				if ("serverAuthCode" in result.result && result.result.serverAuthCode) {
					try {
						await signIn({ code: result.result.serverAuthCode });
						navigate("/");
					} catch {
						// Error already handled by useGoogleSSO's catch
					}
				} else {
					throw Error("No authorization code received from Google");
				}
			} catch (e) {
				trackError("login", {
					type: "SSO",
					provider: "google",
					error: (e as Error).message,
					action: "google oauth login",
				});
				showSnackbar(e instanceof Error ? e.message : t("error.sso.login.failed"), "error");
			}
		} else {
			// Web flow
			googleOAuthLogin();
		}
	};

	return (
		<LoginButton type="button" onClick={handleGoogleSignIn} sx={sx} loading={isPending}>
			<GoogleIcon />
			<Box component="span">{t("continue.with.google")}</Box>
		</LoginButton>
	);
};
